import { forwardRef } from "react";
import { getTemplate, greetingFor, formatDate } from "./templates";

// A branded, print-ready announcement pamphlet rendered entirely with inline
// styles so html2canvas exports it exactly as shown. `profile` is the school
// branding (or a frozen brand_snapshot on a published notice).
const AnnouncementPamphlet = forwardRef(function AnnouncementPamphlet(
  { data = {}, profile = {}, accentOverride },
  ref,
) {
  const t = getTemplate(data.template_id);
  const accent = accentOverride || data.accent_color || t.accent;
  const recipients = data.audience?.recipients || ["students"];
  const schoolName = profile.name || "School Name";
  const hasEvent = data.event_date || data.event_time || data.venue;
  // Indic scripts (Kannada/Devanagari/Tamil/Telugu/Malayalam) break when
  // letter-spacing / uppercase transforms are applied — matras & ottakshara
  // detach from their base glyphs. Detect and disable those on the title.
  const isIndic = /[\u0900-\u0DFF]/.test(data.title || "");

  const optionalLines = [];
  if (profile.show_email && profile.email) optionalLines.push(`Email: ${profile.email}`);
  if (profile.show_website && profile.website) optionalLines.push(profile.website);
  const metaLines = [];
  if (profile.show_affiliation && profile.affiliation_no) metaLines.push(`Affiliation No: ${profile.affiliation_no}`);
  if (profile.show_registration && profile.registration_no) metaLines.push(`Reg. No: ${profile.registration_no}`);

  return (
    <div
      ref={ref}
      data-testid="announcement-pamphlet"
      style={{
        width: "100%",
        background: t.bodyBg,
        fontFamily: `${t.font}, 'Noto Sans Kannada', 'Noto Serif Kannada'`,
        color: "#1e293b",
        border: `1px solid ${accent}22`,
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
      }}
    >
      {/* Mandatory school header */}
      <div
        style={{
          background: t.headerBg,
          color: t.headerText,
          padding: "22px 28px",
          display: "flex",
          alignItems: "center",
          gap: "18px",
          borderBottom: `4px solid ${accent}`,
        }}
      >
        {profile.logo ? (
          <img
            src={profile.logo}
            alt="School logo"
            crossOrigin="anonymous"
            style={{ height: "72px", width: "auto", maxWidth: "96px", objectFit: "contain", flexShrink: 0 }}
          />
        ) : (
          <div style={{ height: "72px", width: "72px", borderRadius: "50%", background: accent + "33", flexShrink: 0 }} />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: "24px", fontWeight: 800, lineHeight: 1.15, letterSpacing: "0.2px" }}>
            {schoolName}
          </div>
          {profile.address && (
            <div style={{ fontSize: "12.5px", opacity: 0.92, marginTop: "4px", lineHeight: 1.35 }}>{profile.address}</div>
          )}
          <div style={{ fontSize: "12.5px", opacity: 0.92, marginTop: "2px" }}>
            Phone: {profile.phone}
            {optionalLines.length > 0 && <span>{"  |  " + optionalLines.join("  |  ")}</span>}
          </div>
          {metaLines.length > 0 && (
            <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "2px" }}>{metaLines.join("   ·   ")}</div>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "26px 30px 18px", flex: 1 }}>
        {data.priority === "important" && (
          <div
            style={{
              display: "inline-block",
              background: "#dc2626",
              color: "#fff",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "1px",
              padding: "3px 10px",
              borderRadius: "4px",
              marginBottom: "12px",
            }}
          >
            IMPORTANT
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          <h1
            style={{
              fontSize: "26px",
              fontWeight: t.titleWeight,
              color: accent,
              fontFamily: `${t.font}, 'Noto Sans Kannada', 'Noto Serif Kannada'`,
              margin: "0 0 4px",
              lineHeight: 1.2,
              textTransform: isIndic ? "none" : "uppercase",
              letterSpacing: isIndic ? "normal" : "0.5px",
              wordBreak: "break-word",
            }}
          >
            {data.title || "Announcement Title"}
          </h1>
          <div style={{ height: "3px", width: "84px", background: accent, margin: "10px auto 0", borderRadius: "2px" }} />
        </div>

        {data.image && (
          <div style={{ margin: "18px 0 4px", textAlign: "center" }}>
            <img
              src={data.image}
              alt="Announcement"
              crossOrigin="anonymous"
              style={{ maxWidth: "100%", maxHeight: "340px", width: "auto", borderRadius: "10px", border: `1px solid ${accent}22`, objectFit: "contain" }}
            />
          </div>
        )}

        <div style={{ fontSize: "12.5px", color: "#64748b", margin: "16px 0 14px" }}>
          <strong>Announcement Date:</strong> {formatDate(data.announcement_date) || "—"}
        </div>

        {data.show_salutation !== false && (
          <div style={{ fontSize: "14px", fontWeight: 600, margin: "6px 0 10px" }}>
            {(data.salutation || "").trim() || greetingFor(recipients)}
          </div>
        )}

        <div
          className="pamphlet-body"
          style={{ fontSize: "14px", lineHeight: 1.7, color: "#1e293b" }}
          dangerouslySetInnerHTML={{ __html: data.description || "<p style='color:#94a3b8'>Announcement content will appear here…</p>" }}
        />

        {hasEvent && (
          <div
            style={{
              marginTop: "18px",
              border: `1px solid ${accent}44`,
              borderLeft: `4px solid ${accent}`,
              borderRadius: "8px",
              background: accent + "0d",
              padding: "14px 16px",
              fontSize: "13.5px",
              lineHeight: 1.9,
            }}
          >
            {data.event_date && <div><strong style={{ color: accent }}>Date:</strong> {data.event_date}</div>}
            {data.event_time && <div><strong style={{ color: accent }}>Time:</strong> {data.event_time}</div>}
            {data.venue && <div><strong style={{ color: accent }}>Venue:</strong> {data.venue}</div>}
          </div>
        )}

        <div style={{ marginTop: "26px", fontSize: "13.5px", lineHeight: 1.6 }}>
          {((data.regards_text === undefined || data.regards_text === null) ? "Regards," : data.regards_text).trim() !== "" && (
            <div>{(data.regards_text === undefined || data.regards_text === null) ? "Regards," : data.regards_text}</div>
          )}
          <div style={{ fontWeight: 700, marginTop: "2px" }}>{data.signoff || profile.principal_name || "Principal"}</div>
          <div style={{ color: "#64748b" }}>{(data.signoff_org || "").trim() || schoolName}</div>
        </div>
      </div>

      {/* Copyright footer */}
      <div
        style={{
          borderTop: `1px solid ${accent}22`,
          padding: "10px 30px",
          fontSize: "10.5px",
          color: "#94a3b8",
          textAlign: "center",
        }}
      >
        © {schoolName} | Built by ZeroLabs Digitals
      </div>
    </div>
  );
});

export default AnnouncementPamphlet;
